import { z } from './utils/zod.js';
export const PLAN_CODES = ['FREE', 'IO', 'ENTERPRISE'];
export const planCodeSchema = z.enum(PLAN_CODES);
export const PLAN_INTERVALS = ['MONTH', 'YEAR'];
export const planIntervalSchema = z.enum(PLAN_INTERVALS);
// Version of the terms whose withdrawal notice a consumer accepts at checkout.
export const WITHDRAWAL_TEXT_VERSION = '2026-10-16';
export const USAGE_METERS = ['AES_SIGNATURE', 'QES_SIGNATURE', 'SMS_OTP', 'EDELIVERY_IDENTITY'];
export const usageMeterSchema = z.enum(USAGE_METERS);
export const ALLOWANCE_PERIODS = ['MONTH', 'YEAR', 'LIFETIME'];
export const STRIPE_METER_EVENT_NAMES = {
    AES_SIGNATURE: 'aes_billable',
    QES_SIGNATURE: 'qes_billable',
    SMS_OTP: 'sms_billable',
    EDELIVERY_IDENTITY: 'edelivery_identity_billable',
};
export const STRIPE_BASE_LOOKUP_KEYS = {
    IO_MONTH: 'io_month',
    IO_YEAR: 'io_year',
};
// IO monthly and yearly share one set of overage prices: overage is always invoiced monthly.
export const STRIPE_OVERAGE_LOOKUP_KEYS = {
    FREE: {
        AES_SIGNATURE: 'overage_free_aes',
        QES_SIGNATURE: 'overage_free_qes',
        SMS_OTP: 'overage_free_sms',
        EDELIVERY_IDENTITY: 'overage_free_edelivery_identity',
    },
    IO: {
        AES_SIGNATURE: 'overage_io_aes',
        QES_SIGNATURE: 'overage_io_qes',
        SMS_OTP: 'overage_io_sms',
        EDELIVERY_IDENTITY: 'overage_io_edelivery_identity',
    },
};
const FREE_OVERAGE = STRIPE_OVERAGE_LOOKUP_KEYS.FREE;
const IO_OVERAGE = STRIPE_OVERAGE_LOOKUP_KEYS.IO;
export const PRICING_CATALOGUE = {
    FREE: {
        code: 'FREE',
        maxUsers: 2,
        pricing: {
            // FREE has no base price in Stripe; a FREE org with a card gets a metered-only subscription.
            MONTH: {
                basePriceCents: 0,
                stripeLookupKey: '',
                allowances: {
                    AES_SIGNATURE: {
                        included: 5,
                        period: 'MONTH',
                        overageCents: 140,
                        stripeLookupKey: FREE_OVERAGE.AES_SIGNATURE,
                    },
                    QES_SIGNATURE: {
                        included: 5,
                        period: 'LIFETIME',
                        overageCents: 490,
                        stripeLookupKey: FREE_OVERAGE.QES_SIGNATURE,
                    },
                    SMS_OTP: { included: 0, period: 'MONTH', overageCents: 70, stripeLookupKey: FREE_OVERAGE.SMS_OTP },
                    EDELIVERY_IDENTITY: {
                        included: 0,
                        period: 'MONTH',
                        overageCents: 790,
                        stripeLookupKey: FREE_OVERAGE.EDELIVERY_IDENTITY,
                    },
                },
            },
        },
    },
    IO: {
        code: 'IO',
        maxUsers: null,
        pricing: {
            MONTH: {
                basePriceCents: 4900,
                stripeLookupKey: STRIPE_BASE_LOOKUP_KEYS.IO_MONTH,
                allowances: {
                    AES_SIGNATURE: {
                        included: 100,
                        period: 'MONTH',
                        overageCents: 90,
                        stripeLookupKey: IO_OVERAGE.AES_SIGNATURE,
                    },
                    QES_SIGNATURE: { included: 5, period: 'MONTH', overageCents: 290, stripeLookupKey: IO_OVERAGE.QES_SIGNATURE },
                    SMS_OTP: { included: 10, period: 'MONTH', overageCents: 70, stripeLookupKey: IO_OVERAGE.SMS_OTP },
                    EDELIVERY_IDENTITY: {
                        included: 0,
                        period: 'MONTH',
                        overageCents: 790,
                        stripeLookupKey: IO_OVERAGE.EDELIVERY_IDENTITY,
                    },
                },
            },
            YEAR: {
                basePriceCents: 58800,
                stripeLookupKey: STRIPE_BASE_LOOKUP_KEYS.IO_YEAR,
                allowances: {
                    AES_SIGNATURE: {
                        included: 1200,
                        period: 'YEAR',
                        overageCents: 90,
                        stripeLookupKey: IO_OVERAGE.AES_SIGNATURE,
                    },
                    QES_SIGNATURE: { included: 60, period: 'YEAR', overageCents: 290, stripeLookupKey: IO_OVERAGE.QES_SIGNATURE },
                    SMS_OTP: { included: 120, period: 'YEAR', overageCents: 70, stripeLookupKey: IO_OVERAGE.SMS_OTP },
                    EDELIVERY_IDENTITY: {
                        included: 0,
                        period: 'YEAR',
                        overageCents: 790,
                        stripeLookupKey: IO_OVERAGE.EDELIVERY_IDENTITY,
                    },
                },
            },
        },
    },
    ENTERPRISE: {
        code: 'ENTERPRISE',
        maxUsers: null,
        pricing: {},
    },
};
export const getPlanPricing = (code, interval) => {
    return PRICING_CATALOGUE[code].pricing[interval];
};
export const listPlanPricings = () => {
    const entries = [];
    for (const code of PLAN_CODES) {
        for (const interval of PLAN_INTERVALS) {
            const pricing = getPlanPricing(code, interval);
            if (pricing) {
                entries.push({ code, interval, pricing });
            }
        }
    }
    return entries;
};
export const getAllPriceLookupKeys = () => {
    const keys = new Set();
    for (const { pricing } of listPlanPricings()) {
        if (pricing.stripeLookupKey) {
            keys.add(pricing.stripeLookupKey);
        }
        for (const meter of USAGE_METERS) {
            keys.add(pricing.allowances[meter].stripeLookupKey);
        }
    }
    return [...keys];
};
const allowanceOverrideSchema = z
    .object({
    included: z.number().int().nonnegative(),
    overageCents: z.number().int().nonnegative(),
})
    .partial();
// Per-organization deviations from the catalogue; a null maxUsers means unlimited users.
export const planOverridesSchema = z
    .object({
    maxUsers: z.number().int().positive().nullable(),
    allowances: z.record(usageMeterSchema, allowanceOverrideSchema),
})
    .partial();
